import { useCallback, useEffect, useRef } from 'react';

type Options = {
  scrollSpeed?: number;
};

/**
 * This hook allows you to scroll an element horizontally with the mouse wheel.
 */
export default function useHorizontalScroll({
  scrollSpeed = 40
}: Options = {}) {
  const containerRef = useRef<HTMLDivElement>(null);

  const accumulatedScroll = useRef(0);
  const animationFrameId = useRef(0);

  const scrollElement = useCallback((element: HTMLDivElement) => {
    // Only scroll if using the wheel. Otherwise, this will break el.scrollIntoView.
    if (accumulatedScroll.current !== 0) {
      element.scrollLeft += accumulatedScroll.current;
    }

    accumulatedScroll.current = 0;
    animationFrameId.current = requestAnimationFrame(() =>
      scrollElement(element)
    );
  }, []);

  useEffect(() => {
    const containerEl = containerRef.current;

    if (!containerEl) return;

    function onWheel(ev: WheelEvent) {
      ev.preventDefault();

      accumulatedScroll.current += ev.deltaY > 0 ? scrollSpeed : -scrollSpeed;
    }

    containerEl.addEventListener('wheel', onWheel, { passive: false });
    animationFrameId.current = requestAnimationFrame(() =>
      scrollElement(containerEl)
    );

    return () => {
      containerEl.removeEventListener('wheel', onWheel);
      cancelAnimationFrame(animationFrameId.current);
    };
  }, [scrollElement, scrollSpeed]);

  return containerRef;
}
